import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  getCurrentFolderStateCached,
  getCurrentPathCached,
  getFilesInCurrentFolderCashed,
  getHasMoreFilesCached,
  getIsCreatingNewFolderCached,
  getIsLoadingMoreCached,
} from '../../../redux/file-manager-component/file-manager-component.selectors';
import { FILE_MANAGER_PAGE_COMPONENT_ID } from '../../../redux/file-manager-component/file-manager-component.types';
import { CurrentFolderState } from '../../../redux/file-manager-component/sub-reducers/current-folder.reducer';
import { FilesState } from '../../../redux/files/files.reducer';
import { getMediaAssetsMap } from '../../../redux/files/files.selectors';
import { StoreState } from '../../../redux/reducers/store.types';

import { getRouteQuery } from '../../../router/router.selectors';
import {
  FileMetadata,
  ImageFileMetadata,
  MediaAsset,
} from '../../../types/media-assets';
import { MediaType } from '@wix/media-platform-js-client/browser';

import { MaybeUndef } from '../../../types/system';
import { isSameArrays } from '../../../utils/array/array';
import { getOperationGroupsList } from '../../operation-panel/operation-groups/operation-groups.selectors';
import {
  OperationGroup,
  OperationGroupType,
} from '../../operation-panel/operation-groups/operation-groups.types';
import { DeleteFilesModalState } from './delete-files-modal.reducer';
import { ExpandModeState } from './expand-mode.reducer';
import { FileManagerPageState } from './file-manager.reducer';

export const getFileManagerPageState = (
  state: StoreState,
): FileManagerPageState => state.fileManagerPage;

export const getCurrentPath = (state: StoreState) =>
  getCurrentPathCached(state, FILE_MANAGER_PAGE_COMPONENT_ID);

export const getLocateFileIdFromRoute = createSelector(
  getRouteQuery,
  (query): MaybeUndef<string> => query && query.locateFileId,
);

const onlyFilesSelected = (
  files: MediaAsset[],
  selectedFileIdsList: string[],
): boolean => {
  return _.every(selectedFileIdsList, (id: string) => {
    const file = _.find(files, { id });
    return file !== undefined && !file.isFolder;
  });
};

export const getSelectedFileIds = createSelector(
  getFileManagerPageState,
  (state: FileManagerPageState): string[] => state.selection.selectedFileIds,
);

export const getFilesInCurrentFolder = (state: StoreState) =>
  getFilesInCurrentFolderCashed(state, FILE_MANAGER_PAGE_COMPONENT_ID);

export const getIsDownloadActionEnabled = createSelector(
  getFilesInCurrentFolder,
  getSelectedFileIds,
  (currentFiles: MediaAsset[], selectedFileIds: string[]): boolean => {
    if (selectedFileIds.length === 0) {
      return false;
    }

    return onlyFilesSelected(currentFiles, selectedFileIds);
  },
);

export const getIsCreatingNewFolder = (store: StoreState): boolean =>
  getIsCreatingNewFolderCached(store, FILE_MANAGER_PAGE_COMPONENT_ID);

export const getCurrentFolder = (store: StoreState) =>
  getCurrentFolderStateCached(store, FILE_MANAGER_PAGE_COMPONENT_ID);

export const getPathFromRoute = createSelector(
  getRouteQuery,
  (query): string => (query && query.path) || '/',
);

export const getCurrentUrlPathIsLoaded = createSelector(
  getCurrentFolder,
  getPathFromRoute,
  (current: CurrentFolderState, routePath: string): boolean =>
    current.path === routePath && current.loadState.fulfilled,
);

export const getIsCreateNewFolderActionEnabled = getCurrentUrlPathIsLoaded;

export const getIsUploadFilesToCurrentFolderActionEnabled =
  getCurrentUrlPathIsLoaded;

export const getIsDeleteCurrentFolderActionEnabled = createSelector(
  getCurrentUrlPathIsLoaded,
  getPathFromRoute,
  (isCurrentFolderLoaded: boolean, path: string): boolean => {
    return isCurrentFolderLoaded && path !== '/';
  },
);

export const getOnlyImageMeta = (
  metadata: MaybeUndef<FileMetadata>,
): MaybeUndef<ImageFileMetadata> => {
  return metadata && metadata.mediaType === MediaType.Image
    ? (metadata as ImageFileMetadata)
    : undefined;
};

export const isAlreadyDownloadingBatch = (
  state: StoreState,
  fileIds: string[],
): boolean => {
  return _.some(
    getOperationGroupsList(state),
    (group: OperationGroup) =>
      group.type === OperationGroupType.MultipleDownload &&
      isSameArrays(fileIds, group.fileIdsList),
  );
};

export const getExpandModeState = createSelector(
  getFileManagerPageState,
  (state: FileManagerPageState): ExpandModeState => state.expandMode,
);

export const getIsInExpandMode = createSelector(
  getExpandModeState,
  (state): boolean => Boolean(state.fileId),
);

export const getFileInExpandMode = createSelector(
  getExpandModeState,
  getMediaAssetsMap,
  ({ fileId }, mediaAssetsMap): MediaAsset => {
    if (!fileId || !mediaAssetsMap[fileId]) {
      throw new Error(
        'File id for expand mode is not specified or file is not found or not loaded',
      );
    }

    return mediaAssetsMap[fileId];
  },
);

const getFileDeleteModalState = createSelector(
  getFileManagerPageState,
  (state: FileManagerPageState): DeleteFilesModalState =>
    state.deleteFilesModal,
);

export const getIsDeleteModalOpened = createSelector(
  getFileDeleteModalState,
  (state: DeleteFilesModalState) => state.isDeleteModalOpened,
);

export const isLoadingMore = (store: StoreState) =>
  getIsLoadingMoreCached(store, FILE_MANAGER_PAGE_COMPONENT_ID);

export const hasMoreFiles = (store: StoreState) =>
  getHasMoreFilesCached(store, FILE_MANAGER_PAGE_COMPONENT_ID);

const getFileId = (state: StoreState, props): string => props.fileId;

export const makeGetFileById = () =>
  createSelector(
    getMediaAssetsMap,
    getFileId,
    (state: FilesState, fileId: string) => state[fileId],
  );

export const makeIsFileSelected = () =>
  createSelector(
    getSelectedFileIds,
    getFileId,
    (selectedIds: string[], fileId: string) => _.includes(selectedIds, fileId),
  );

export const getSelectedFiles = createSelector(
  getFilesInCurrentFolder,
  getSelectedFileIds,
  (files: MediaAsset[], selectedIds: string[]): ArrayLike<MediaAsset> =>
    _.chain(selectedIds)
      .map((id) => _.find(files, { id }))
      .compact()
      .value(),
);
